// JoshSkills page helpers (T-351, owner request 2026-11-03): data from the JoshSkills read-only API
// (joshskills/api.py on 127.0.0.1:10010; local dashboard reaches it via the Vite proxy rule '/api/joshskills').
// Pure functions are tested with node --test (frontend/tests/joshskills.test.mjs). No writes, no orders.

const BASE = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) || ''

/** GET a JoshSkills API path with the dashboard login token (the public relay requires it, T-351); throws with the
 *  server's error text on a non-2xx answer. The local Vite proxy ignores the header. */
export async function jsGet(path) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('sf_auth_token') : null
  const r = await fetch(`${BASE}/api/joshskills${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  const body = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`)
  return body
}

export const EXIT_TONE = { target: '#34d399', stop: '#f87171', flat_by: '#94a3b8', flat_lastbar: '#94a3b8' }
export const VERDICT_TONE = { 'toward target': '#34d399', 'coin flip': '#fbbf24', 'toward stop': '#f87171', unknown: '#94a3b8' }

/** Money with sign, e.g. +$12.30 / -$4.00; '—' for null. */
export function money(v) {
  if (v == null || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  return `${n < 0 ? '-' : '+'}$${Math.abs(n).toFixed(2)}`
}

/** R multiple with sign, e.g. +0.56R. */
export function rText(v) {
  if (v == null || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}R`
}

/** Seconds to add to a UTC epoch so lightweight-charts (which draws UTC) shows New York wall time. */
export function etShift(epochSec) {
  const d = new Date(epochSec * 1000)
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(d).filter(p => p.type !== 'literal').map(p => [p.type, p.value]))
  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second)
  return Math.round((asUtc - d.getTime()) / 1000)
}

/** Bars from the API -> candle data in New York wall time. */
export function toCandles(bars) {
  return (bars || []).map(b => ({ time: b.time + etShift(b.time), open: b.open, high: b.high, low: b.low, close: b.close }))
}

/** Entry / exit markers for one trade, snapped to the 5-minute bar that holds each moment (wall time). */
export function tradeMarkers(t) {
  const out = []
  const snap = (iso) => { const e = Math.floor(new Date(iso).getTime() / 1000); const b = e - (e % 300); return b + etShift(b) }
  if (t.entry_time) out.push({ time: snap(t.entry_time) - 300, position: 'belowBar', color: '#38bdf8', shape: 'arrowUp', text: `BUY ${Number(t.entry_price).toFixed(2)}` })
  if (t.exit_time) out.push({ time: snap(t.exit_time) - (t.exit_reason === 'flat_by' ? 0 : 300), position: 'aboveBar', color: EXIT_TONE[t.exit_reason] || '#94a3b8', shape: 'arrowDown', text: `${(t.exit_reason || '').toUpperCase()} ${Number(t.exit_price).toFixed(2)}` })
  return out.sort((a, b) => a.time - b.time)
}

/** Summary line for the stats strip. */
export function statsLine(s) {
  if (!s || !s.closed) return 'no closed trades yet'
  const wr = Math.round((s.wins / s.closed) * 100)
  return `${s.closed} closed · ${s.wins} won (${wr}%) · avg ${rText(s.avg_r)} · native ${money(s.pnl)} · could-have-filled ${money(s.alt_pnl)}`
}

// --- T-364 page redesign (2026-11-04): status strip helpers and the validated chart palette ---
// Mark colours validated with the dataviz skill's validator on the page surface #111827 (dark): native #0891b2 vs
// could-have #d97706 (CVD dE 19.0), target #199e70 vs stop #d95926 (CVD dE 9.4). The old #34d399 / #f87171 pair failed
// colour-blind separation (dE 5.6 deutan), so chart marks use these; text keeps the text tones.
export const MARK = { native: '#0891b2', alt: '#d97706', target: '#199e70', stop: '#d95926', neither: '#475569', entry: '#0891b2' }

/** New York "YYYY-MM-DD" and "HH:MM" for a Date. */
export function etParts(now = new Date()) {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
  const p = Object.fromEntries(f.formatToParts(now).map(x => [x.type, x.value]))
  return { day: `${p.year}-${p.month}-${p.day}`, hm: `${p.hour === '24' ? '00' : p.hour}:${p.minute}` }
}

/** Entry window state from the executor's limits: 'before' | 'open' | 'after' (weekends are not special-cased). */
export function windowState(limits, now = new Date()) {
  if (!limits) return 'unknown'
  const { hm } = etParts(now)
  return hm < limits.entry_start ? 'before' : hm < limits.entry_end ? 'open' : 'after'
}

/** Today's real paper orders (live trades sent to Alpaca): entries, open, closed, realised P&L. */
export function todayLive(trades, now = new Date()) {
  const { day } = etParts(now)
  const t = (trades || []).filter(x => String(x.trade_date).slice(0, 10) === day && x.broker_status)
  const filled = t.filter(x => x.broker_entry_price != null)
  const closed = t.filter(x => x.broker_status === 'closed')
  return { sent: t.length, filled: filled.length, open: filled.length - closed.length, closed: closed.length,
           pnl: closed.reduce((a, x) => a + (Number(x.broker_pnl) || 0), 0) }
}

/** "4 min ago" style age of a timestamp (null -> 'never'). */
export function ageText(ts, now = new Date()) {
  if (!ts) return 'never'
  const s = Math.round((now - new Date(String(ts).replace(' ', 'T'))) / 1000)
  if (Number.isNaN(s)) return '—'
  return s < 90 ? `${Math.max(s, 0)} s ago` : s < 5400 ? `${Math.round(s / 60)} min ago` : s < 172800 ? `${Math.round(s / 3600)} h ago` : `${Math.round(s / 86400)} d ago`
}
